// ─── Iter194 — Phase C: Property status + ownership transfer ─────────
// Compact card that lives on the Property Detail page. Two responsibilities:
//   1. Render the property's current status (active / shadowed / inactive)
//      with a colour-coded pill and a dropdown to change it (writes via
//      PATCH /api/properties/{id}/status, persists a `status_history`
//      entry with the operator's reason).
//   2. "Transfer ownership" button → modal picks a new client + transfer
//      date. Writes via POST /api/properties/{id}/transfer-ownership and
//      pushes an `ownership_history` entry (audit trail).
import React, { useState, useCallback, useMemo } from 'react';
import { CircleDot, Building2, ArrowRightLeft, History, Loader2, X, Eye, EyeOff, Ban } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const STATUS_LABEL = {
  active:   { label: 'Active',   tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200',  icon: Eye,    desc: 'Visible in default property lists. New appraisals allowed.' },
  shadowed: { label: 'Shadowed', tone: 'bg-amber-50 text-amber-800 ring-amber-200',        icon: EyeOff, desc: 'Hidden from default lists. Historic appraisals remain accessible. New appraisals still allowed.' },
  inactive: { label: 'Inactive', tone: 'bg-rose-50 text-rose-700 ring-rose-200',           icon: Ban,    desc: 'Hidden from default lists. New appraisals BLOCKED until reactivated.' },
};

export default function PropertyStatusCard({ propertyId, data, clients = [], onUpdated }) {
  const { api } = useAuth();
  const status = data?.status || 'active';
  const meta = STATUS_LABEL[status] || STATUS_LABEL.active;
  const StatusIcon = meta.icon;
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [reason, setReason] = useState('');
  // Iter195 — gate Inactive transitions behind an explicit confirmation
  // because Inactive blocks new appraisal creation. Holds the pending
  // target status string while the modal is open; null when no
  // confirmation is required.
  const [pendingStatus, setPendingStatus] = useState(null);
  const ownershipHistory = useMemo(() => Array.isArray(data?.ownership_history) ? data.ownership_history : [], [data]);
  const statusHistory    = useMemo(() => Array.isArray(data?.status_history)    ? data.status_history    : [], [data]);

  const applyStatus = useCallback(async (next) => {
    setBusy(true);
    try {
      const { data: updated } = await api.patch(`/properties/${propertyId}/status`, { status: next, reason });
      toast.success(`Property marked ${STATUS_LABEL[next]?.label || next}`);
      setReason('');
      setPendingStatus(null);
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Status change failed');
    } finally {
      setBusy(false);
    }
  }, [api, propertyId, reason, onUpdated]);

  const changeStatus = useCallback((next) => {
    if (next === status) return;
    // Iter195 — guard the destructive transition. Inactive prevents
    // new appraisals being created against the property, so require an
    // explicit "Yes, mark inactive" confirmation step.
    if (next === 'inactive') {
      setPendingStatus(next);
      return;
    }
    applyStatus(next);
  }, [status, applyStatus]);

  return (
    <div data-testid="property-status-card" className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-3">
      <div className="flex items-center gap-2">
        <CircleDot size={16} className="text-[#D35400]" />
        <h3 className="text-sm font-bold text-[#2C2C2C]">Status & Ownership</h3>
        <span
          data-testid="property-status-pill"
          className={`ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ring-1 ${meta.tone}`}
          title={meta.desc}
        >
          <StatusIcon size={10} /> {meta.label}
        </span>
        <button
          data-testid="property-history-toggle"
          onClick={() => setShowHistory(v => !v)}
          className="ml-auto text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1"
          title="View status + ownership history"
        >
          <History size={11} /> {showHistory ? 'Hide' : 'History'}
          {ownershipHistory.length + statusHistory.length > 0 && (
            <span className="px-1.5 py-0 rounded-full bg-slate-100 font-mono text-[10px]">
              {ownershipHistory.length + statusHistory.length}
            </span>
          )}
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {['active', 'shadowed', 'inactive'].map(s => {
          const m = STATUS_LABEL[s];
          return (
            <button
              key={s}
              data-testid={`property-status-set-${s}`}
              onClick={() => changeStatus(s)}
              disabled={busy || s === status}
              className={`text-xs font-semibold px-2 py-1 rounded border transition ${s === status ? `${m.tone} cursor-default` : 'border-slate-300 text-slate-700 hover:bg-slate-50'} disabled:opacity-100 disabled:cursor-default`}
              title={m.desc}
            >{s === status ? '✓ ' : ''}{m.label}</button>
          );
        })}
        <input
          data-testid="property-status-reason"
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Reason (optional, stored in history)"
          className="flex-1 min-w-[180px] text-xs border border-slate-300 rounded px-2 py-1"
        />
        <button
          data-testid="property-transfer-btn"
          onClick={() => setShowTransfer(true)}
          className="text-xs font-semibold px-3 py-1 rounded border border-sky-400 text-sky-700 hover:bg-sky-50 flex items-center gap-1"
        ><ArrowRightLeft size={11} /> Transfer ownership</button>
      </div>

      <p className="text-[11px] text-slate-500 italic">{meta.desc}</p>

      {showHistory && (
        <div data-testid="property-history-panel" className="border-t border-slate-200 pt-3 space-y-2 text-xs">
          {ownershipHistory.length === 0 && statusHistory.length === 0 && (
            <p className="text-slate-500 italic">No history yet.</p>
          )}
          {ownershipHistory.length > 0 && (
            <div>
              <p className="font-semibold text-slate-700 mb-1 flex items-center gap-1"><Building2 size={11} /> Ownership transfers</p>
              <ul className="space-y-1">
                {ownershipHistory.map((h, i) => (
                  <li key={`oh-${i}`} className="text-slate-700 font-mono text-[11px]">
                    <span className="text-slate-500">{(h.transferred_at || '').slice(0, 10)}</span>
                    {' · '}<strong>{h.from_client_name || h.from_client_id || '—'}</strong>
                    {' → '}<strong className="text-emerald-700">{h.to_client_name || h.to_client_id || '—'}</strong>
                    {h.notes ? <span className="text-slate-400"> · "{h.notes}"</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {statusHistory.length > 0 && (
            <div>
              <p className="font-semibold text-slate-700 mb-1 flex items-center gap-1"><CircleDot size={11} /> Status changes</p>
              <ul className="space-y-1">
                {statusHistory.map((h, i) => (
                  <li key={`sh-${i}`} className="text-slate-700 font-mono text-[11px]">
                    <span className="text-slate-500">{(h.changed_at || '').slice(0, 10)}</span>
                    {' · '}<strong>{h.from_status}</strong> → <strong className="text-amber-700">{h.to_status}</strong>
                    {h.reason ? <span className="text-slate-400"> · "{h.reason}"</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {showTransfer && (
        <TransferOwnershipModal
          api={api}
          propertyId={propertyId}
          currentClientId={data?.client_id || ''}
          clients={clients}
          onClose={() => setShowTransfer(false)}
          onTransferred={(updated) => { setShowTransfer(false); onUpdated?.(updated); }}
        />
      )}

      {pendingStatus === 'inactive' && (
        <div
          data-testid="property-inactive-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setPendingStatus(null); }}
        >
          <div className="bg-white rounded-lg w-full max-w-md p-5 space-y-4 shadow-2xl">
            <div className="flex items-center gap-2">
              <Ban size={18} className="text-rose-600" />
              <h3 className="font-bold text-slate-900">Mark property Inactive?</h3>
              <button onClick={() => setPendingStatus(null)} disabled={busy} className="ml-auto text-slate-400 hover:text-slate-700 disabled:opacity-50"><X size={16} /></button>
            </div>
            <div className="space-y-2 text-sm text-slate-700">
              <p>
                Inactive properties are hidden from default lists and <strong className="text-rose-700">block all new appraisal creation</strong> until reactivated.
              </p>
              <p className="text-xs text-slate-500">Historic appraisals stay accessible. You can switch back to Active at any time.</p>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Reason (recorded in status history)</label>
              <input
                data-testid="property-inactive-reason"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g. Property demolished, owner deceased…"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2 justify-end pt-2">
              <button
                data-testid="property-inactive-cancel"
                onClick={() => setPendingStatus(null)}
                disabled={busy}
                className="px-3 py-1.5 text-sm font-semibold border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
              >Cancel</button>
              <button
                data-testid="property-inactive-confirm"
                onClick={() => applyStatus('inactive')}
                disabled={busy}
                className="flex items-center gap-1 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded text-sm font-semibold disabled:opacity-50"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
                Yes, mark Inactive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TransferOwnershipModal({ api, propertyId, currentClientId, clients, onClose, onTransferred }) {
  const [newClientId, setNewClientId] = useState('');
  const [transferDate, setTransferDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!newClientId) {
      toast.error('Pick a new owner first');
      return;
    }
    if (newClientId === currentClientId) {
      toast.error('Property is already owned by this client');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/transfer-ownership`, {
        new_client_id: newClientId, transfer_date: transferDate, notes,
      });
      toast.success('Ownership transferred. Historic appraisals stay linked to the previous owner.');
      onTransferred?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transfer failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="property-transfer-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg w-full max-w-md p-5 space-y-4 shadow-2xl">
        <div className="flex items-center gap-2">
          <ArrowRightLeft size={18} className="text-sky-700" />
          <h3 className="font-bold text-slate-900">Transfer ownership</h3>
          <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <p className="text-xs text-slate-500">
          Historic appraisals on this property keep their original client (audit trail). Only the
          property's primary owner flips, so future appraisals land under the new owner.
        </p>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">New owner</label>
          <select
            data-testid="transfer-new-client"
            value={newClientId}
            onChange={e => setNewClientId(e.target.value)}
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
          >
            <option value="">— Pick a client —</option>
            {clients
              .filter(c => (c._id || c.id) !== currentClientId)
              .map(c => {
                const id = c._id || c.id;
                const name = `${c.name_first || ''} ${c.name_last || ''}`.trim() || c.email || id;
                return <option key={id} value={id}>{name}</option>;
              })}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Transfer date</label>
          <input data-testid="transfer-date" type="date" value={transferDate} onChange={e => setTransferDate(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Notes (optional)</label>
          <textarea data-testid="transfer-notes" rows={2} value={notes} onChange={e => setNotes(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm resize-y" placeholder="Sale settled, new owner contacted, …" />
        </div>
        <div className="flex items-center gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm font-semibold border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
          <button
            data-testid="transfer-submit"
            onClick={submit}
            disabled={busy || !newClientId}
            className="flex items-center gap-1 px-3 py-1.5 bg-sky-700 hover:bg-sky-800 text-white rounded text-sm font-semibold disabled:opacity-50"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <ArrowRightLeft size={12} />}
            Transfer
          </button>
        </div>
      </div>
    </div>
  );
}
